import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import GameCanvas from '../components/GameCanvas';
import { EventTracker } from '@adgf/analytics';
import type { Game } from '@adgf/shared';
import { GameStatus } from '@adgf/shared';
import DemoGame from '../games/DemoGame';

const DEMO_GAME: Game = {
  id: '11111111-1111-1111-1111-111111111111',
  slug: 'demo-clicker',
  title: 'Demo Clicker',
  description: 'A simple clicker game to demonstrate the Phaser runtime environment.',
  category: 'idle',
  template_id: null,
  version: '0.1.0',
  status: GameStatus.PUBLISHED,
  difficulty: 'easy',
  ai_score: 0,
  retention_score: 0,
  monetization_score: 0,
  created_at: '2026-09-10T00:00:00.000Z',
  published_at: '2026-09-10T00:00:00.000Z',
};

export default function GamePage() {
  const { slug } = useParams<{ slug: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [tracker] = useState(() => new EventTracker({ enabled: true, debug: false }));

  useEffect(() => {
    // M0: Use demo game. M1 will fetch from /api/v1/games/:slug
    if (slug === 'demo-clicker') {
      setGame(DEMO_GAME);
      tracker.track('game_open', { gameId: DEMO_GAME.id });
      tracker.startSession();
    } else {
      setGame(null);
    }

    return () => {
      tracker.endSession();
      tracker.destroy();
    };
  }, [slug, tracker]);

  if (!game) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <h2>Game Not Found</h2>
        <p style={{ color: 'var(--color-text-muted)', margin: '16px 0' }}>
          The game &quot;{slug}&quot; does not exist or has not been published.
        </p>
        <Link to="/">Back to Home</Link>
      </div>
    );
  }

  const handleGameStart = () => {
    tracker.track('game_start', { gameId: game.id });
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/" style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
          &larr; Back to Games
        </Link>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.75rem', marginBottom: 4 }}>{game.title}</h2>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: 12 }}>{game.description}</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span
            style={{
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--color-accent)',
              textTransform: 'capitalize',
            }}
          >
            {game.category}
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--color-accent)',
              textTransform: 'capitalize',
            }}
          >
            {game.difficulty}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            v{game.version}
          </span>
        </div>
      </div>

      <GameCanvas
        gameId={game.id}
        config={{ width: 800, height: 600 }}
        events={{
          onGameStart: handleGameStart,
        }}
        scenes={[new DemoGame()]}
      />

      <div style={{ marginTop: 24, padding: 16, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)' }}>
        <h4 style={{ fontSize: '0.9rem', marginBottom: 8 }}>How to Play</h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          Click the button as fast as you can to increase your score. This is a M0 demo game
          showcasing the Phaser runtime integration.
        </p>
      </div>
    </div>
  );
}
